// src/modulos/Parametros/AccountPlanPage.js
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';
import {
    PageHeader,
    SearchBar,
    TableSkeleton,
    Pagination,
    FormModal,
    ConfirmModal,
    Toast,
    ComboBox
} from '../../components/common';
import { FiEdit, FiTrash2 } from 'react-icons/fi';

const ACCOUNT_PLAN_COLLECTION = 'parameters/data/account-plan';
const ITEMS_PER_PAGE = 10;

const AccountPlanPage = () => {
    const { t } = useTranslation();
    const can = usePagePermissions('plano_de_contas');
    const { data: accounts, loading, error, addDocument, updateDocument, deleteDocument } = useFirestore(ACCOUNT_PLAN_COLLECTION);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [formLoading, setFormLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [toast, setToast] = useState(null);

    // Form state
    const [parentAccountId, setParentAccountId] = useState('');
    const [type, setType] = useState('');
    const [description, setDescription] = useState('');
    const [canTransfer, setCanTransfer] = useState(false);
    const [generatedCode, setGeneratedCode] = useState('');

    // Account types with their fixed first digit - use translations
    const ACCOUNT_TYPES = [
        { value: 'ATIVO', label: t('accountPlan.type.ATIVO'), prefix: '1' },
        { value: 'PASSIVO', label: t('accountPlan.type.PASSIVO'), prefix: '2' },
        { value: 'DESPESA', label: t('accountPlan.type.DESPESA'), prefix: '3' },
        { value: 'RECEITA', label: t('accountPlan.type.RECEITA'), prefix: '4' },
        { value: 'CUSTO', label: t('accountPlan.type.CUSTO'), prefix: '5' },
        { value: 'INVESTIMENTO', label: t('accountPlan.type.INVESTIMENTO'), prefix: '6' }
    ];

    // Helper to generate sequential code with dot notation
    const generateCode = (accountsList, selectedType, parentId = null) => {
        if (!selectedType) return '';

        const typeConfig = ACCOUNT_TYPES.find(t => t.value === selectedType);
        if (!typeConfig) return '';

        if (parentId) {
            const parentAccount = accountsList.find(acc => acc.id === parentId);
            if (parentAccount && parentAccount.code) {
                const siblings = accountsList.filter(acc =>
                    acc.parentAccountId === parentId &&
                    acc.type === selectedType
                );

                let maxChildNumber = 0;
                siblings.forEach(sibling => {
                    if (sibling.code && sibling.code.startsWith(parentAccount.code + '.')) {
                        const parts = sibling.code.split('.');
                        const lastPart = parseInt(parts[parts.length - 1], 10);
                        if (!isNaN(lastPart) && lastPart > maxChildNumber) {
                            maxChildNumber = lastPart;
                        }
                    }
                });

                const nextChildNumber = maxChildNumber + 1;
                return `${parentAccount.code}.${nextChildNumber}`;
            }
        }

        const existingRoot = accountsList.find(acc =>
            acc.type === selectedType &&
            !acc.parentAccountId &&
            acc.code === typeConfig.prefix
        );

        if (existingRoot) {
            return null;
        }

        return typeConfig.prefix;
    };

    React.useEffect(() => {
        if (!editingItem && accounts) {
            const newCode = generateCode(accounts, type, parentAccountId);
            setGeneratedCode(newCode || '');
        }
    }, [type, parentAccountId, accounts, editingItem]);

    const buildHierarchy = (items, parentId = null, level = 0) => {
        return items
            .filter(item => item.parentAccountId === parentId)
            .sort((a, b) => {
                const aParts = (a.code || '').split('.').map(Number);
                const bParts = (b.code || '').split('.').map(Number);
                const maxLen = Math.max(aParts.length, bParts.length);
                for (let i = 0; i < maxLen; i++) {
                    const aVal = i < aParts.length ? aParts[i] : 0;
                    const bVal = i < bParts.length ? bParts[i] : 0;
                    if (aVal !== bVal) return aVal - bVal;
                }
                return 0;
            })
            .map(item => ({
                ...item,
                level,
                children: buildHierarchy(items, item.id, level + 1)
            }));
    };

    const flattenHierarchy = (items, flatList = []) => {
        items.forEach(item => {
            flatList.push(item);
            if (item.children && item.children.length > 0) {
                flattenHierarchy(item.children, flatList);
            }
        });
        return flatList;
    };

    const hierarchicalData = useMemo(() => {
        if (!accounts.length) return [];
        return buildHierarchy(accounts);
    }, [accounts]);

    const filteredAndFlattened = useMemo(() => {
        const flattened = flattenHierarchy([...hierarchicalData]);

        if (!searchTerm.trim()) return flattened;

        const searchLower = searchTerm.toLowerCase();
        return flattened.filter(item =>
            item.description?.toLowerCase().includes(searchLower) ||
            item.code?.toLowerCase().includes(searchLower) ||
            ACCOUNT_TYPES.find(t => t.value === item.type)?.label.toLowerCase().includes(searchLower)
        );
    }, [hierarchicalData, searchTerm]);

    const totalPages = Math.ceil(filteredAndFlattened.length / ITEMS_PER_PAGE);
    const paginatedItems = filteredAndFlattened.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    const getParentOptions = () => {
        if (!accounts.length) return [];

        const excludeId = editingItem?.id;

        const getDescendantIds = (itemId, itemsList) => {
            const ids = [itemId];
            const children = itemsList.filter(item => item.parentAccountId === itemId);
            children.forEach(child => {
                ids.push(...getDescendantIds(child.id, itemsList));
            });
            return ids;
        };

        const excludedIds = excludeId ? getDescendantIds(excludeId, accounts) : [];

        const buildOptions = (items, level = 0, options = []) => {
            items.forEach(item => {
                if (!excludedIds.includes(item.id)) {
                    const prefix = '— '.repeat(level);
                    options.push({
                        id: item.id,
                        label: `${prefix}${item.code} - ${item.description}`,
                        level,
                        type: item.type
                    });
                    if (item.children && item.children.length) {
                        buildOptions(item.children, level + 1, options);
                    }
                } else if (item.children && item.children.length) {
                    buildOptions(item.children, level + 1, options);
                }
            });
            return options;
        };

        return buildOptions(hierarchicalData);
    };

    const parentOptions = getParentOptions();

    const filteredParentOptions = useMemo(() => {
        if (!type) return parentOptions;
        return parentOptions.filter(opt => opt.type === type);
    }, [parentOptions, type]);

    const handleOpenModal = (item = null) => {
        setEditingItem(item);
        if (item) {
            setParentAccountId(item.parentAccountId || '');
            setType(item.type || '');
            setDescription(item.description || '');
            setCanTransfer(item.canTransfer || false);
            setGeneratedCode(item.code || '');
        } else {
            setParentAccountId('');
            setType('');
            setDescription('');
            setCanTransfer(false);
            setGeneratedCode('');
        }
        setIsModalOpen(true);
    };

    const resetForm = () => {
        setParentAccountId('');
        setType('');
        setDescription('');
        setCanTransfer(false);
        setGeneratedCode('');
    };

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
    };

    const validateForm = () => {
        if (!type) {
            showToast(t('accountPlan.form.selectType'), 'error');
            return false;
        }
        if (!description.trim()) {
            showToast(t('accountPlan.form.descriptionRequired'), 'error');
            return false;
        }

        if (!editingItem) {
            if (!generatedCode) {
                const typeLabel = ACCOUNT_TYPES.find(t => t.value === type)?.label;
                showToast(t('accountPlan.form.rootExists', { type: typeLabel }), 'error');
                return false;
            }
            const existingCode = accounts.find(acc => acc.code === generatedCode);
            if (existingCode) {
                showToast(t('accountPlan.form.codeExists', { code: generatedCode }), 'error');
                return false;
            }
        }

        return true;
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        setFormLoading(true);

        const payload = {
            parentAccountId: parentAccountId || null,
            type,
            description: description.trim(),
            canTransfer,
            updatedAt: new Date()
        };

        if (!editingItem) {
            payload.code = generatedCode;
            payload.createdAt = new Date();
        }

        try {
            if (editingItem) {
                await updateDocument(editingItem.id, payload);
                showToast(t('accountPlan.messages.updateSuccess'), 'success');
            } else {
                await addDocument(payload);
                showToast(t('accountPlan.messages.createSuccess'), 'success');
            }
            setIsModalOpen(false);
            resetForm();
        } catch (err) {
            console.error("Erro ao salvar Plano de Conta:", err);
            showToast(t('accountPlan.messages.saveError'), 'error');
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteClick = (id) => {
        const hasChildren = accounts.some(acc => acc.parentAccountId === id);

        if (hasChildren) {
            showToast(t('accountPlan.messages.hasChildren'), 'warning');
            return;
        }

        setItemToDelete(id);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        setDeleteLoading(true);
        try {
            await deleteDocument(itemToDelete);
            showToast(t('accountPlan.messages.deleteSuccess'), 'success');
            setShowDeleteModal(false);
            setItemToDelete(null);
        } catch (err) {
            console.error("Erro ao excluir Plano de Conta:", err);
            showToast(t('accountPlan.messages.deleteError'), 'error');
        } finally {
            setDeleteLoading(false);
        }
    };

    const renderIndentation = (level) => {
        if (level === 0) return null;
        return (
            <span style={{ display: 'inline-block', width: level * 24, marginRight: 4 }}>
                {'↳'}
            </span>
        );
    };

    const getTypeLabel = (typeValue) => {
        const found = ACCOUNT_TYPES.find(t => t.value === typeValue);
        return found?.label || typeValue;
    };

    const renderCanTransfer = (value) => {
        return value ?
            <span style={{ color: '#2e7d32' }}>{t('accountPlan.status.yes')}</span> :
            <span style={{ color: '#999' }}>{t('accountPlan.status.no')}</span>;
    };

    // Table styles
    const tableStyles = {
        container: {
            overflowX: 'auto',
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            backgroundColor: '#fff',
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '14px',
        },
        th: {
            padding: '12px 16px',
            textAlign: 'left',
            backgroundColor: '#f5f5f5',
            fontWeight: 600,
            color: '#333',
            borderBottom: '2px solid #e0e0e0',
        },
        td: {
            padding: '12px 16px',
            borderBottom: '1px solid #f0f0f0',
            verticalAlign: 'middle',
        },
        actionButton: {
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px 8px',
            borderRadius: '4px',
            transition: 'background-color 0.2s',
        },
        editButton: {
            color: '#1976d2',
        },
        deleteButton: {
            color: '#d32f2f',
        },
        emptyState: {
            textAlign: 'center',
            padding: '40px',
            color: '#999',
        }
    };

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            <PageHeader
                title={t('accountPlan.title')}
                actionText={t('accountPlan.newAccount')}
                onAction={() => handleOpenModal()}
                disabled={!can.create}
                showAction={can.create}
            />

            <div style={{ marginBottom: '16px' }}>
                <SearchBar
                    placeholder={t('accountPlan.searchPlaceholder')}
                    onSearch={(term) => {
                        setSearchTerm(term);
                        setCurrentPage(1);
                    }}
                />
            </div>

            <div style={tableStyles.container}>
                <table style={tableStyles.table}>
                    <thead>
                        <tr>
                            <th style={{ ...tableStyles.th, width: '120px' }}>{t('accountPlan.table.code')}</th>
                            <th style={tableStyles.th}>{t('accountPlan.table.description')}</th>
                            <th style={{ ...tableStyles.th, width: '120px' }}>{t('accountPlan.table.type')}</th>
                            <th style={{ ...tableStyles.th, width: '150px' }}>{t('accountPlan.table.parentAccount')}</th>
                            <th style={{ ...tableStyles.th, width: '140px' }}>{t('accountPlan.table.movement')}</th>
                            <th style={{ ...tableStyles.th, width: '150px' }}>{t('accountPlan.table.actions')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="6">
                                    <TableSkeleton columns={6} rows={5} />
                                </td>
                            </tr>
                        ) : paginatedItems.length > 0 ? (
                            paginatedItems.map(item => {
                                const parent = accounts.find(acc => acc.id === item.parentAccountId);
                                return (
                                    <tr key={item.id}>
                                        <td style={{ ...tableStyles.td, fontFamily: 'monospace', fontWeight: 'bold' }}>
                                            {renderIndentation(item.level)}
                                            {item.code}
                                        </td>
                                        <td style={tableStyles.td}>{item.description}</td>
                                        <td style={tableStyles.td}>{getTypeLabel(item.type)}</td>
                                        <td style={tableStyles.td}>{parent ? `${parent.code} - ${parent.description}` : '—'}</td>
                                        <td style={tableStyles.td}>{renderCanTransfer(item.canTransfer)}</td>
                                        <td style={tableStyles.td}>
                                            {can.edit && (
                                                <button
                                                    onClick={() => handleOpenModal(item)}
                                                    style={{
                                                        ...tableStyles.actionButton,
                                                        ...tableStyles.editButton
                                                    }}
                                                >
                                                    <FiEdit size={18} />
                                                </button>
                                            )}
                                            {can.delete && (
                                                <button
                                                    onClick={() => handleDeleteClick(item.id)}
                                                    style={{
                                                        ...tableStyles.actionButton,
                                                        ...tableStyles.deleteButton
                                                    }}
                                                >
                                                    <FiTrash2 size={18} />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="6" style={tableStyles.emptyState}>
                                    {searchTerm ? t('accountPlan.table.emptySearch') : t('accountPlan.table.empty')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div style={{ marginTop: '16px' }}>
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredAndFlattened.length}
                    onPageChange={setCurrentPage}
                    itemsLabel={t('accountPlan.pagination.items')}
                />
            </div>

            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    resetForm();
                }}
                onSubmit={handleSave}
                title={editingItem ? t('accountPlan.editAccountTitle') : t('accountPlan.newAccountTitle')}
                isLoading={formLoading}
                submitText={formLoading ? t('accountPlan.form.saving') : t('accountPlan.form.save')}
                cancelText={t('accountPlan.form.cancel')}
                maxWidth="860px"
            >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    {!editingItem && (
                        <div style={{ gridColumn: 'span 1' }}>
                            <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '14px' }}>
                                {t('accountPlan.form.code')}
                            </label>
                            <input
                                type="text"
                                value={generatedCode || t('accountPlan.form.codePlaceholder')}
                                disabled
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    backgroundColor: '#f5f5f5',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    fontFamily: 'monospace',
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    color: generatedCode ? '#000' : '#999',
                                    cursor: 'not-allowed'
                                }}
                            />
                            <small style={{ color: '#666', fontSize: 11, marginTop: 4, display: 'block' }}>
                                {!parentAccountId ?
                                    t('accountPlan.form.codeRootHint') :
                                    t('accountPlan.form.codeChildHint')}
                            </small>
                        </div>
                    )}

                    {editingItem && (
                        <div style={{ gridColumn: 'span 1' }}>
                            <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '14px' }}>
                                {t('accountPlan.form.code')}
                            </label>
                            <input
                                type="text"
                                value={editingItem.code}
                                disabled
                                style={{
                                    width: '100%',
                                    padding: '10px 12px',
                                    backgroundColor: '#f5f5f5',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    fontFamily: 'monospace',
                                    fontSize: '14px',
                                    cursor: 'not-allowed'
                                }}
                            />
                        </div>
                    )}

                    <div style={{ gridColumn: 'span 1' }}>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '14px' }}>
                            {t('accountPlan.form.type')}
                        </label>
                        <select
                            value={type}
                            onChange={e => {
                                setType(e.target.value);
                                setParentAccountId('');
                            }}
                            disabled={!!editingItem}
                            required
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                backgroundColor: editingItem ? '#f5f5f5' : '#fff',
                                fontSize: '14px',
                                cursor: editingItem ? 'not-allowed' : 'pointer'
                            }}
                        >
                            <option value="">{t('accountPlan.form.typePlaceholder')}</option>
                            {ACCOUNT_TYPES.map(accountType => (
                                <option key={accountType.value} value={accountType.value}>
                                    {accountType.label}
                                </option>
                            ))}
                        </select>
                        {editingItem && (
                            <small style={{ color: '#f44336', fontSize: 11, marginTop: 4, display: 'block' }}>
                                {t('accountPlan.form.typeLocked')}
                            </small>
                        )}
                    </div>

                    <div style={{ gridColumn: 'span 1' }}>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '14px' }}>
                            {t('accountPlan.form.parentAccount')}
                        </label>
                        <select
                            value={parentAccountId}
                            onChange={e => setParentAccountId(e.target.value)}
                            disabled={!type}
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                backgroundColor: type ? '#fff' : '#f5f5f5',
                                fontSize: '14px',
                                cursor: type ? 'pointer' : 'not-allowed'
                            }}
                        >
                            <option value="">{t('accountPlan.form.parentNone')}</option>
                            {filteredParentOptions.map(opt => (
                                <option key={opt.id} value={opt.id}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        {!type && (
                            <small style={{ color: '#f44336', fontSize: 11, marginTop: 4, display: 'block' }}>
                                {t('accountPlan.form.parentSelectTypeFirst')}
                            </small>
                        )}
                        {type && filteredParentOptions.length === 0 && parentAccountId === '' && (
                            <small style={{ color: '#ff9800', fontSize: 11, marginTop: 4, display: 'block' }}>
                                {t('accountPlan.form.parentNoOptions')}
                            </small>
                        )}
                    </div>

                    <div style={{ gridColumn: 'span 2' }}>
                        <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', fontSize: '14px' }}>
                            {t('accountPlan.form.description')}
                        </label>
                        <input
                            type="text"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder={t('accountPlan.form.descriptionPlaceholder')}
                            required
                            autoFocus
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                border: '1px solid #ddd',
                                borderRadius: '6px',
                                fontSize: '14px'
                            }}
                        />
                    </div>

                    <div style={{ gridColumn: 'span 2' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={canTransfer}
                                onChange={e => setCanTransfer(e.target.checked)}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '14px' }}>{t('accountPlan.form.canTransfer')}</span>
                        </label>
                        <small style={{ color: '#666', fontSize: 11, marginTop: 4, display: 'block', marginLeft: '26px' }}>
                            {t('accountPlan.form.canTransferHint')}
                        </small>
                    </div>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setItemToDelete(null);
                }}
                onConfirm={handleConfirmDelete}
                title={t('accountPlan.deleteConfirmTitle')}
                message={t('accountPlan.messages.deleteConfirm')}
                confirmText={t('accountPlan.delete')}
                cancelText={t('accountPlan.cancel')}
                danger
                isLoading={deleteLoading}
            />
        </div>
    );
};

export default AccountPlanPage;